import React, { useState } from 'react';
import { useOnboardingViewport } from '../shared/useViewport';
import { supabase } from '../../../supabaseClient';

export const PlanCreationScreen = ({ onNext, onBack, user }) => {
  const vp = useOnboardingViewport();
  const [selectedPlan, setSelectedPlan] = useState('monthly');

  const handlePayment = async () => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
      const response = await fetch(`${SUPABASE_URL}/functions/v1/create-checkout-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          user: user,
          plan: selectedPlan
        })
      });

      const data = await response.json();


      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        console.error("No checkout URL received", data);
      }
    } catch (err) {
      console.error("Payment error:", err);
    }
  };

  const isDesktop = vp.isTablet;

  const colors = {
    primary: '#14B8A6',
    accent: '#14B8A6',
    text: '#0F172A',
    secondaryText: '#64748B',
    cardBorder: '#E2E8F0',
    highlightBg: '#F0FDFB'
  };

  const getCardStyle = (planId) => {
    const isSelected = selectedPlan === planId;
    return {
      flex: 1,
      padding: vp.sp(16, 20, 24),
      borderRadius: '24px',
      background: '#FFFFFF',
      border: isSelected ? `2px solid ${colors.primary}` : `1px solid ${colors.cardBorder}`,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      position: 'relative',
      boxShadow: isSelected ? '0 8px 24px rgba(0,121,130,0.12)' : '0 4px 12px rgba(0,0,0,0.03)',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      transform: 'none'
    };
  };

  const checkItemStyle = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    width: '100%',
    marginBottom: '8px'
  };

  const checkIcon = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={colors.accent} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      width: '100%',
      maxWidth: vp.canvasMaxWidth,
      margin: isDesktop ? '0 auto' : undefined,
      background: 'transparent',
      justifyContent: 'flex-start',
      paddingBottom: '20px',
      marginTop: vp.sp(-30, -40, -50), // Aggressive shift up to align with Page 7
      position: 'relative' // Ensure absolute children are relative to this screen
    }}>
      {/* Top Section: Headline + Mascot */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        marginTop: 0,
        position: 'relative',
        minHeight: vp.sp(220, 240, 260)
      }}>
        <div style={{ flex: 1, paddingTop: vp.sp(15, 20, 25), zIndex: 2 }}>
          <h1 style={{
            fontSize: vp.sp(32, 36, 40),
            fontWeight: '900',
            color: colors.text,
            lineHeight: '1.1',
            margin: 0,
            letterSpacing: '-1.5px'
          }}>
            Make vocab<br />
            <span style={{
              color: colors.primary,
              position: 'relative',
              display: 'inline-block'
            }}>
              finally stick.
              <svg
                style={{
                  position: 'absolute',
                  bottom: '-14px',
                  left: 0,
                  width: '100%',
                  height: '8px'
                }}
                viewBox="0 0 100 10"
                preserveAspectRatio="none"
              >
                <path d="M0 5 Q 50 10 100 5" stroke="#FBBF24" strokeWidth="4" fill="none" strokeLinecap="round" />
              </svg>
            </span>
          </h1>
          <p style={{
            fontSize: vp.sp(14, 15, 16),
            color: colors.secondaryText,
            lineHeight: '1.5',
            marginTop: '20px',
            fontWeight: '500',
            maxWidth: '240px'
          }}>
            Unlock unlimited access and build real verbal confidence—every single day.
          </p>
        </div>

        {/* Mascot Image */}
        <div style={{
          position: 'absolute',
          right: vp.sp(-25, -35, -45),
          top: vp.sp(35, 45, 55),
          width: vp.sp(198, 242, 286),
          zIndex: 1
        }}>
          <img
            src="/images/onboarding/pay.webp"
            alt="Mascot"
            style={{ width: '100%', height: 'auto' }}
          />
        </div>
      </div>

      {/* Coffee Comparison Line */}
      <div style={{
        marginTop: vp.sp(10, 20, 30),
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        padding: '8px 0'
      }}>
        <span style={{
          fontSize: vp.sp(13, 14, 15),
          color: colors.secondaryText,
          fontWeight: '500',
          opacity: 0.9
        }}>
          Small daily practice. Long-term verbal confidence.
        </span>
      </div>

      {/* Trust/Trial Section */}
      <div style={{
        marginTop: vp.sp(16, 20, 24),
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
      }}>
        <div style={{ fontSize: vp.sp(16, 18, 20), fontWeight: '800', color: colors.primary }}>✨3-day free trial included✨</div>
        <div style={{ fontSize: vp.sp(13, 14, 15), color: colors.secondaryText, fontWeight: '500' }}>No charge today • Cancel anytime</div>
      </div>

      {/* Pricing Cards Grid */}
      <div style={{
        display: 'flex',
        gap: vp.sp(12, 16, 20),
        marginTop: vp.sp(16, 20, 24),
        width: '100%',
        alignItems: 'stretch'
      }}>
        {/* Monthly Card */}
        <div
          style={getCardStyle('monthly')}
          onClick={() => setSelectedPlan('monthly')}
        >
          <div style={{ fontSize: '12px', fontWeight: '800', color: selectedPlan === 'monthly' ? colors.primary : colors.secondaryText, letterSpacing: '1px', marginBottom: '12px' }}>MONTHLY</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '2px' }}>
            <span style={{ fontSize: '20px', fontWeight: '900', color: colors.text }}>$</span>
            <span style={{ fontSize: '32px', fontWeight: '900', color: colors.text }}>4.99</span>
          </div>
          <div style={{ fontSize: '13px', color: colors.secondaryText, marginBottom: '4px' }}>/month</div>
          <div style={{
            fontSize: '11px',
            fontWeight: '600',
            color: colors.secondaryText,
            marginBottom: '12px',
            opacity: 0.8
          }}>
            Billed monthly
          </div>

          <div style={{ width: '100%', height: '1px', background: colors.cardBorder, marginBottom: '16px' }} />

          <div style={checkItemStyle}>
            {checkIcon}
            <span style={{ fontSize: '11px', fontWeight: '600', color: colors.text }}>Unlimited access</span>
          </div>
          <div style={checkItemStyle}>
            {checkIcon}
            <span style={{ fontSize: '11px', fontWeight: '600', color: colors.text }}>Adaptive games</span>
          </div>
          <div style={checkItemStyle}>
            {checkIcon}
            <span style={{ fontSize: '11px', fontWeight: '600', color: colors.text }}>Weak-word review</span>
          </div>
        </div>

        {/* Quarterly Card */}
        <div
          style={getCardStyle('quarterly')}
          onClick={() => setSelectedPlan('quarterly')}
        >
          {/* Best Value Ribbon */}
          <div style={{
            position: 'absolute',
            top: '-14px',
            background: colors.primary,
            color: 'white',
            padding: '4px 12px',
            borderRadius: '10px',
            fontSize: '11px',
            fontWeight: '900',
            boxShadow: '0 4px 8px rgba(0,121,130,0.2)'
          }}>
            BEST VALUE
          </div>

          <div style={{ fontSize: '12px', fontWeight: '800', color: colors.primary, letterSpacing: '1px', marginBottom: '12px' }}>QUARTERLY</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '2px' }}>
            <span style={{ fontSize: '20px', fontWeight: '900', color: colors.primary }}>$</span>
            <span style={{ fontSize: '32px', fontWeight: '900', color: colors.primary }}>11.99</span>
          </div>
          <div style={{ fontSize: '13px', color: colors.secondaryText, marginBottom: '4px', fontWeight: '700' }}>every 3 months</div>

          <div style={{
            fontSize: '12px',
            fontWeight: '800',
            color: colors.primary,
            marginBottom: '12px',
            background: colors.highlightBg,
            padding: '2px 8px',
            borderRadius: '6px'
          }}>
            Only $3.99/month
          </div>

          <div style={{ width: '100%', height: '1px', background: colors.cardBorder, marginBottom: '16px' }} />

          <div style={checkItemStyle}>
            {checkIcon}
            <span style={{ fontSize: '11px', fontWeight: '600', color: colors.text }}>Everything in Monthly</span>
          </div>
          <div style={checkItemStyle}>
            {checkIcon}
            <span style={{ fontSize: '11px', fontWeight: '600', color: colors.text }}>Save <span style={{ color: colors.primary, fontWeight: '800' }}>20%</span></span>
          </div>
          <div style={checkItemStyle}>
            {checkIcon}
            <span style={{ fontSize: '11px', fontWeight: '600', color: colors.text }}>No ads</span>
          </div>
        </div>
      </div>

      {/* Local Currency Note */}
      <div style={{
        marginTop: vp.sp(16, 20, 24),
        textAlign: 'center',
        padding: '0 20px'
      }}>
        <span style={{
          fontSize: '11px',
          color: colors.secondaryText,
          fontWeight: '500',
          opacity: 0.7
        }}>
          Prices shown in USD • Converted to your local currency at checkout
        </span>
      </div>

      {/* Bottom CTA — Now part of scroll flow */}
      <div style={{
        marginTop: vp.sp(16, 20, 24),
        width: '100%',
        zIndex: 10
      }}>
        <button
          onClick={handlePayment}
          style={{
            width: '100%',
            height: `${vp.spacing.ctaHeight}px`,
            background: '#14B8A6',
            borderRadius: `${vp.spacing.ctaBorderRadius}px`,
            border: 'none',
            color: 'white',
            fontSize: `${vp.spacing.ctaFontSize * 0.92}px`,
            fontWeight: '900',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            cursor: 'pointer',
            boxShadow: '0 8px 166px rgba(20, 184, 166, 0.2)',
            transition: 'transform 0.2s'
          }}
        >
          Start 3-Day Free Trial
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </button>
        <div style={{
          marginTop: '12px',
          textAlign: 'center',
          fontSize: '12px',
          color: colors.secondaryText,
          fontWeight: '500',
          opacity: 0.8
        }}>
          Cancel anytime during the trial
        </div>
      </div>
    </div>
  );
};

export default PlanCreationScreen;
